import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/ui/Header';
import Button from '../components/ui/Button';
import Icon from '../components/AppIcon';
import { calendarService } from '../services/calendarService';
import { realtimeService } from '../services/realtimeService';
import { format, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameMonth, isSameDay, parseISO, addMonths, subMonths } from 'date-fns';

const CalendarDashboard = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'week'
  const [realtimeChannel, setRealtimeChannel] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    
    if (userProfile?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    loadCalendarData();
    setupRealtimeSubscription();

    return () => {
      if (realtimeChannel) {
        realtimeService?.unsubscribe(realtimeChannel);
      }
    };
  }, [user, userProfile, navigate]);

  useEffect(() => {
    loadCalendarData();
  }, [currentMonth]);

  const loadCalendarData = async () => {
    try {
      setLoading(true);
      const monthStart = startOfMonth(currentMonth);
      const monthEnd = endOfMonth(currentMonth);
      const startDate = startOfWeek(monthStart)?.toISOString();
      const endDate = endOfWeek(monthEnd)?.toISOString();
      
      const [slotsData, bookingsData] = await Promise.all([
        calendarService?.getAdminAvailabilitySlots(startDate, endDate),
        calendarService?.getAllBookings()
      ]);
      
      setSlots(slotsData || []);
      setBookings(bookingsData || []);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = realtimeService?.subscribeToConsultationBookings(
      (newBooking) => {
        console.log('New consultation booking:', newBooking);
        setBookings(prev => [{
          id: newBooking?.id,
          clientId: newBooking?.client_id,
          slotId: newBooking?.slot_id,
          title: newBooking?.title,
          description: newBooking?.description,
          clientName: newBooking?.client_name,
          clientEmail: newBooking?.client_email,
          clientPhone: newBooking?.client_phone,
          company: newBooking?.company,
          serviceInterest: newBooking?.service_interest,
          scheduledStart: newBooking?.scheduled_start,
          scheduledEnd: newBooking?.scheduled_end,
          durationMinutes: newBooking?.duration_minutes,
          status: newBooking?.status,
          meetingNotes: newBooking?.meeting_notes,
          createdAt: newBooking?.created_at,
          updatedAt: newBooking?.updated_at
        }, ...prev]);
        setToast({
          message: `New booking: ${newBooking?.client_name}`,
          type: 'success'
        });
        setTimeout(() => setToast(null), 5000);
      },
      (updatedBooking) => {
        console.log('Consultation booking updated:', updatedBooking);
        setBookings(prev => 
          prev?.map(booking => 
            booking?.id === updatedBooking?.id ? {
              id: updatedBooking?.id,
              clientId: updatedBooking?.client_id,
              slotId: updatedBooking?.slot_id,
              title: updatedBooking?.title,
              description: updatedBooking?.description,
              clientName: updatedBooking?.client_name,
              clientEmail: updatedBooking?.client_email,
              clientPhone: updatedBooking?.client_phone,
              company: updatedBooking?.company,
              serviceInterest: updatedBooking?.service_interest,
              scheduledStart: updatedBooking?.scheduled_start,
              scheduledEnd: updatedBooking?.scheduled_end,
              durationMinutes: updatedBooking?.duration_minutes,
              status: updatedBooking?.status,
              meetingNotes: updatedBooking?.meeting_notes,
              createdAt: updatedBooking?.created_at,
              updatedAt: updatedBooking?.updated_at
            } : booking
          )
        );
        setToast({
          message: `Booking updated: ${updatedBooking?.client_name} - ${updatedBooking?.status}`,
          type: 'info'
        });
        setTimeout(() => setToast(null), 5000);
      },
      (deletedBooking) => {
        console.log('Consultation booking deleted:', deletedBooking);
        setBookings(prev => prev?.filter(b => b?.id !== deletedBooking?.id));
        setToast({
          message: `Booking cancelled: ${deletedBooking?.client_name}`,
          type: 'warning'
        });
        setTimeout(() => setToast(null), 5000);
      }
    );

    setRealtimeChannel(channel);
  };

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const renderCalendarHeader = () => {
    const dateFormat = 'MMMM yyyy';
    return (
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold text-foreground">
            {format(currentMonth, dateFormat)}
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={goToToday}
          >
            Today
          </Button>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={prevMonth}
            iconName="ChevronLeft"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={nextMonth}
            iconName="ChevronRight"
          />
        </div>
      </div>
    );
  };

  const renderDaysOfWeek = () => {
    let days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return (
      <div className="grid grid-cols-7 gap-2 mb-2">
        {days?.map(day => (
          <div key={day} className="text-center text-sm font-semibold text-muted-foreground py-2">
            {day}
          </div>
        ))}
      </div>
    );
  };

  const getDateSlots = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return slots?.filter(slot => {
      const slotDate = format(parseISO(slot?.startTime), 'yyyy-MM-dd');
      return slotDate === dateStr;
    });
  };

  const getDateBookings = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return bookings?.filter(booking => {
      const bookingDate = format(parseISO(booking?.scheduledStart), 'yyyy-MM-dd');
      return bookingDate === dateStr;
    });
  };

  const renderCalendarDays = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const formattedDate = format(day, 'd');
        const cloneDay = day;
        const daySlots = getDateSlots(day);
        const dayBookings = getDateBookings(day);
        const freeSlots = daySlots?.filter(s => s?.isAvailable)?.length || 0;
        const bookedSlots = dayBookings?.filter(b => b?.status === 'confirmed' || b?.status === 'pending')?.length || 0;

        days?.push(
          <div
            key={day}
            className={`min-h-[120px] border rounded-lg p-2 cursor-pointer transition-all hover:shadow-md ${
              !isSameMonth(day, monthStart)
                ? 'bg-gray-50 text-gray-400'
                : isSameDay(day, new Date())
                ? 'bg-primary/5 border-primary' :'bg-white hover:bg-surface'
            } ${
              selectedDate && isSameDay(day, selectedDate)
                ? 'ring-2 ring-primary' :''
            }`}
            onClick={() => setSelectedDate(cloneDay)}
          >
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-semibold ${
                isSameDay(day, new Date()) ? 'text-primary' : ''
              }`}>
                {formattedDate}
              </span>
              {(freeSlots > 0 || bookedSlots > 0) && (
                <div className="flex items-center space-x-1">
                  {freeSlots > 0 && (
                    <span className="flex items-center space-x-1 text-xs text-green-600">
                      <Icon name="Circle" size={8} className="fill-green-600" />
                      <span>{freeSlots}</span>
                    </span>
                  )}
                  {bookedSlots > 0 && (
                    <span className="flex items-center space-x-1 text-xs text-blue-600">
                      <Icon name="CheckCircle" size={8} className="fill-blue-600" />
                      <span>{bookedSlots}</span>
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-1">
              {daySlots?.slice(0, 2)?.map(slot => (
                <div
                  key={slot?.id}
                  className={`text-xs px-2 py-1 rounded truncate ${
                    slot?.isAvailable
                      ? 'bg-green-100 text-green-800' :'bg-gray-100 text-gray-600'
                  }`}
                >
                  {format(parseISO(slot?.startTime), 'HH:mm')}
                </div>
              ))}
              {dayBookings?.slice(0, 2)?.map(booking => (
                <div
                  key={booking?.id}
                  className={`text-xs px-2 py-1 rounded truncate ${
                    booking?.status === 'confirmed' ?'bg-blue-100 text-blue-800'
                      : booking?.status === 'pending' ?'bg-yellow-100 text-yellow-800' :'bg-gray-100 text-gray-600'
                  }`}
                >
                  {format(parseISO(booking?.scheduledStart), 'HH:mm')} - {booking?.clientName}
                </div>
              ))}
              {(daySlots?.length + dayBookings?.length > 4) && (
                <div className="text-xs text-muted-foreground text-center">
                  +{daySlots?.length + dayBookings?.length - 4} more
                </div>
              )}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows?.push(
        <div key={day} className="grid grid-cols-7 gap-2">
          {days}
        </div>
      );
      days = [];
    }
    return <div className="space-y-2">{rows}</div>;
  };

  const renderSelectedDateDetails = () => {
    if (!selectedDate) return null;

    const daySlots = getDateSlots(selectedDate);
    const dayBookings = getDateBookings(selectedDate);

    return (
      <div className="mt-6 bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-foreground">
            {format(selectedDate, 'EEEE, MMMM d, yyyy')}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedDate(null)}
            iconName="X"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Free Slots */}
          <div>
            <h4 className="text-lg font-semibold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="Circle" size={16} className="text-green-600" />
              <span>Free Slots ({daySlots?.filter(s => s?.isAvailable)?.length})</span>
            </h4>
            <div className="space-y-2">
              {daySlots?.filter(s => s?.isAvailable)?.length === 0 ? (
                <p className="text-sm text-muted-foreground">No free slots available</p>
              ) : (
                daySlots?.filter(s => s?.isAvailable)?.map(slot => (
                  <div key={slot?.id} className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-green-800">
                          {format(parseISO(slot?.startTime), 'h:mm a')} - {format(parseISO(slot?.endTime), 'h:mm a')}
                        </p>
                        <p className="text-xs text-green-600 mt-1">Available for booking</p>
                      </div>
                      <Icon name="Clock" size={20} className="text-green-600" />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Booked Slots */}
          <div>
            <h4 className="text-lg font-semibold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} className="text-blue-600" />
              <span>Booked Slots ({dayBookings?.length})</span>
            </h4>
            <div className="space-y-2">
              {dayBookings?.length === 0 ? (
                <p className="text-sm text-muted-foreground">No bookings for this day</p>
              ) : (
                dayBookings?.map(booking => (
                  <div key={booking?.id} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-blue-800">
                        {format(parseISO(booking?.scheduledStart), 'h:mm a')} - {format(parseISO(booking?.scheduledEnd), 'h:mm a')}
                      </p>
                      <span className={`text-xs px-2 py-1 rounded ${
                        booking?.status === 'confirmed' ?'bg-green-100 text-green-800'
                          : booking?.status === 'pending' ?'bg-yellow-100 text-yellow-800'
                          : booking?.status === 'cancelled' ?'bg-red-100 text-red-800' :'bg-gray-100 text-gray-800'
                      }`}>
                        {booking?.status}
                      </span>
                    </div>
                    <p className="text-sm text-blue-900 font-medium">{booking?.clientName}</p>
                    <p className="text-xs text-blue-600 mt-1">{booking?.clientEmail}</p>
                    {booking?.company && (
                      <p className="text-xs text-blue-600">{booking?.company}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStats = () => {
    const totalSlots = slots?.length || 0;
    const freeSlots = slots?.filter(s => s?.isAvailable)?.length || 0;
    const totalBookings = bookings?.length || 0;
    const confirmedBookings = bookings?.filter(b => b?.status === 'confirmed')?.length || 0;
    const pendingBookings = bookings?.filter(b => b?.status === 'pending')?.length || 0;

    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Icon name="Calendar" size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{totalSlots}</p>
              <p className="text-xs text-muted-foreground">Total Slots</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Icon name="Circle" size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{freeSlots}</p>
              <p className="text-xs text-muted-foreground">Free Slots</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Icon name="CheckCircle" size={24} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{totalBookings}</p>
              <p className="text-xs text-muted-foreground">Total Bookings</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Icon name="Check" size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{confirmedBookings}</p>
              <p className="text-xs text-muted-foreground">Confirmed</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Icon name="Clock" size={24} className="text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{pendingBookings}</p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-20 pb-12 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading calendar...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Calendar Dashboard | North Star Analytics</title>
        <meta name="description" content="View and manage consultation bookings and availability slots" />
      </Helmet>
      
      {/* Real-time notification toast */}
      {toast && (
        <div className="fixed top-20 right-4 z-50 animate-slide-in-right">
          <div className={`rounded-lg shadow-lg p-4 max-w-sm ${
            toast?.type === 'success' ? 'bg-green-600' :
            toast?.type === 'info' ? 'bg-blue-600' :
            toast?.type === 'warning' ? 'bg-yellow-600' : 'bg-gray-600'
          } text-white`}>
            <div className="flex items-start gap-3">
              <Icon name="Bell" className="w-5 h-5 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">{toast?.message}</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  <p className="text-xs opacity-90">Live Update</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="min-h-screen bg-background">
        <Header />
        
        <div className="pt-20 pb-12 px-4">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                    Calendar Dashboard
                  </h1>
                  <p className="text-muted-foreground">
                    View booked consultations and free availability slots
                  </p>
                </div>
                <div className="flex items-center gap-2 bg-green-50 px-4 py-2 rounded-lg">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-green-700">Live Updates Active</span>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertCircle" size={20} className="text-red-600" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            {/* Stats */}
            {renderStats()}

            {/* Legend */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-6">
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center space-x-2">
                  <Icon name="Circle" size={12} className="text-green-600 fill-green-600" />
                  <span className="text-sm text-muted-foreground">Free Slots</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={12} className="text-blue-600 fill-blue-600" />
                  <span className="text-sm text-muted-foreground">Booked Slots</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded bg-primary/20 border border-primary"></div>
                  <span className="text-sm text-muted-foreground">Today</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded ring-2 ring-primary"></div>
                  <span className="text-sm text-muted-foreground">Selected Date</span>
                </div>
              </div>
            </div>

            {/* Calendar */}
            <div className="bg-white rounded-lg shadow-md p-6">
              {renderCalendarHeader()}
              {renderDaysOfWeek()}
              {renderCalendarDays()}
            </div>

            {/* Selected Date Details */}
            {renderSelectedDateDetails()}
          </div>
        </div>
      </div>
    </>
  );
};

export default CalendarDashboard;